import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG, loadAppConfig } from '../../config/apiConfig'
import { showToast } from "../../utils/messageUtil"
import { useUser } from '../../contexts/UserContext'
import './index.css'

const getStatusText = (status) => {
    switch (status) {
        case 0: return '上级审批通过'
        case 1: return '待还车'
        case 2: return '用车审批驳回'
        case 3: return '还车待确认'
        case 4: return '已结束'
        case 5: return '还车不规范'
        case 6: return '取消用车'
        case 7: return '待上级审批'
        case 8: return '已撤销申请'
        case 9: return '上级审批驳回'
        default: return '未知状态'
    }
}

const getStatusClass = (status) => {
    switch (status) {
        case 1: case 7: return 'status-pending'
        case 0: return 'status-approved'
        case 8: case 5: case 9: return 'status-cancelled'
        case 4: case 6: case 2: return 'status-completed'
        default: return ''
    }
}

export default function PrivateCarReport() {
    const navigate = useNavigate()
    const { userInfo, loading: userLoading, isNewFeatureExperience, checkingNewFeatureExperience, checkNewFeatureExperienceStatus, isDirectDispatch } = useUser()
    const [reports, setReports] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [filters, setFilters] = useState({
        keyword: '',
        applyStartTime: '',
        applyEndTime: ''
    })
    const [detailModalVisible, setDetailModalVisible] = useState(false)
    const [selectedReport, setSelectedReport] = useState(null)
    const [traceLoading, setTraceLoading] = useState({})
    const containerRef = useRef(null)

    // 权限守卫
    useEffect(() => {
        if (userLoading) return
        const uid = userInfo?.open_id || userInfo?.user_id
        if (uid) checkNewFeatureExperienceStatus(uid)
    }, [userLoading, userInfo, checkNewFeatureExperienceStatus])

    // 【2026-08-22 开放所有用户】移除新功能体验名单守卫（功能全量开放，不再弹回首页）
    /* useEffect(() => {
        if (userLoading || checkingNewFeatureExperience) return
        if (!isNewFeatureExperience) {
            showToast('您不在新功能体验名单中，不能访问', 'error')
            navigate('/', { replace: true })
        }
    }, [userLoading, checkingNewFeatureExperience, isNewFeatureExperience, navigate]) */

    // 【2026-08-22 私车报表仅 isDirectDispatch 权限用户可见】页面级权限守卫（防 URL 直达越权）
    useEffect(() => {
        if (userLoading) return
        if (!isDirectDispatch) {
            showToast('您没有私车报表权限', 'error')
            navigate('/', { replace: true })
        }
    }, [userLoading, isDirectDispatch, navigate])

    // 获取私车报表（申请类型=1，全公司）
    const fetchReports = async (page = 1) => {
        try {
            if (page === 1) setLoading(true); else setLoadingMore(true)
            const formatDateTime = (dateStr, isEnd = false) => {
                if (!dateStr) return null;
                return isEnd ? `${dateStr} 23:59:59` : `${dateStr} 00:00:00`;
            };
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getPrivateCarReportList`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicationType: 1,  // 只查私车申请
                page: page,
                rows: 10,
                keyword: filters.keyword || undefined,
                applyStartTime: formatDateTime(filters.applyStartTime),
                applyEndTime: formatDateTime(filters.applyEndTime, true)
            }, {
                headers: { 'Content-Type': 'application/json' }
            })

            if (response.data && response.data.success) {
                const data = response.data.result.rows || []
                const records = response.data.result.records || 0
                const rowsPerPage = 10
                const calculatedTotalPages = Math.ceil(records / rowsPerPage)
                setTotalPages(calculatedTotalPages)
                setCurrentPage(page)
                setHasMore(page < calculatedTotalPages)
                if (page === 1) {
                    setReports(data)
                } else {
                    setReports(prev => [...prev, ...data])
                }
            } else {
                if (page === 1) setReports([])
            }
        } catch (error) {
            console.error('获取私车报表失败:', error)
            if (page === 1) setReports([])
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }

    // 初始化 + 筛选变化
    useEffect(() => {
        (async () => {
            await loadAppConfig();
            fetchReports(1);
        })();
    }, [filters])

    // 加载更多
    const handleLoadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            fetchReports(currentPage + 1)
        }
    }, [loadingMore, hasMore, currentPage])

    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current
            if (!container || loadingMore || !hasMore) return
            const { scrollTop, scrollHeight, clientHeight } = container
            if (scrollHeight - scrollTop - clientHeight < 50) handleLoadMore()
        }
        const container = containerRef.current
        if (container) container.addEventListener('scroll', handleScroll)
        return () => { if (container) container.removeEventListener('scroll', handleScroll) }
    }, [loadingMore, hasMore, handleLoadMore])

    const handleFilterChange = (e) => {
        const { name, value } = e.target
        setFilters(prev => ({ ...prev, [name]: value }))
    }

    const handleResetFilters = () => {
        setFilters({ keyword: '', applyStartTime: '', applyEndTime: '' })
    }

    // 导出私车报表（符合当前检索条件，applicationType=1 只导私车）
    const handleExport = async () => {
        try {
            showToast('正在导出，请稍候...', 'info')

            const storedUserInfo = localStorage.getItem('userInfo')
            if (!storedUserInfo) {
                showToast('用户信息丢失，请重新登录', 'error')
                return
            }
            JSON.parse(storedUserInfo)

            const timestamp = new Date().getTime()
            const fileName = `私车报表_${timestamp}.xlsx`

            // 与列表查询保持一致：date 值补齐时分秒
            const formatDateTime = (dateStr, isEnd = false) => {
                if (!dateStr) return null;
                return isEnd ? `${dateStr} 23:59:59` : `${dateStr} 00:00:00`;
            };

            const exportParams = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicationType: 1, // 只导私车申请
                keyword: filters.keyword || undefined,
                applyStartTime: formatDateTime(filters.applyStartTime),
                applyEndTime: formatDateTime(filters.applyEndTime, true),
                _paging: false // 导出全部数据
            }

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/exportPrivateCarReport`, exportParams, {
                headers: {
                    'Content-Type': 'application/json'
                },
                responseType: 'blob' // 重要：设置响应类型为blob
            })

            // 后端返回 JSON 错误体（HTTP200）时 blob 类型为 json，按错误处理避免下载坏文件
            const blobType = (response.data && response.data.type) || ''
            if (response.status === 200 && response.data && !blobType.toLowerCase().includes('json')) {
                const blob = response.data
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', fileName)
                link.style.display = 'none'

                document.body.appendChild(link)
                link.click()

                setTimeout(() => {
                    document.body.removeChild(link)
                    window.URL.revokeObjectURL(url)
                }, 100)

                showToast('导出成功，文件正在下载', 'success')
            } else if (response.data && blobType.toLowerCase().includes('json')) {
                // 解析 JSON 错误体，提示后端返回的具体错误
                try {
                    const text = await response.data.text()
                    const errData = JSON.parse(text)
                    showToast(`导出失败: ${errData.message || '未知错误'}`, 'error')
                } catch (parseError) {
                    console.error('解析导出错误响应失败:', parseError)
                    showToast('导出失败，请稍后重试', 'error')
                }
            } else {
                showToast('导出失败，请稍后重试', 'error')
            }
        } catch (error) {
            console.error('导出失败:', error)
            showToast(`导出失败: ${error.message || '未知错误'}`, 'error')
        }
    }

    // 用车详情弹窗
    const handleReportDetail = (report) => {
        setSelectedReport(report)
        setDetailModalVisible(true)
    }
    const handleCloseModal = () => {
        setDetailModalVisible(false)
        setSelectedReport(null)
    }

    // 车辆轨迹：获取 18gps 轨迹回放 URL，成功后直接新窗口打开（飞书 WebView 内禁止内嵌 iframe，改为 window.open）
    const handlePlayback = async (record) => {
        const applicationId = record.applicationId
        if (traceLoading[applicationId]) return
        setTraceLoading(prev => ({ ...prev, [applicationId]: true }))
        try {
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getPrivateCarPlaybackUrl`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicationId: applicationId
            }, {
                headers: { 'Content-Type': 'application/json' }
            })
            if (response.data && response.data.code === 200 && response.data.data) {
                window.open(response.data.data, '_blank')
                showToast('已在浏览器打开回放页', 'success')
            } else {
                showToast(response.data?.message || '获取轨迹失败', 'error')
            }
        } catch (error) {
            console.error('获取车辆轨迹失败:', error)
            showToast('网络错误，请稍后重试', 'error')
        } finally {
            setTraceLoading(prev => ({ ...prev, [applicationId]: false }))
        }
    }

    return (
        <div className="private-car-report-page" ref={containerRef}>
            <div className="pcrt-header">
                {/*<button className="pcrt-back" onClick={() => navigate('/')}>← 返回</button>*/}
                <h2>私车报表</h2>
            </div>

            {/* 筛选 */}
            <div className="pcrt-filters">
                <input
                    className="pcrt-filter-input"
                    placeholder="检索：imei、车牌号、申请单号、申请人"
                    name="keyword"
                    value={filters.keyword}
                    onChange={handleFilterChange}
                />
                <div className="pcrt-date-row">
                    <input
                        className="pcrt-date-input"
                        type="date"
                        name="applyStartTime"
                        value={filters.applyStartTime}
                        onChange={handleFilterChange}
                    />
                    <span className="pcrt-date-sep">至</span>
                    <input
                        className="pcrt-date-input"
                        type="date"
                        name="applyEndTime"
                        value={filters.applyEndTime}
                        onChange={handleFilterChange}
                    />
                </div>
                <div className="pcrt-btn-row">
                    <button className="pcrt-search-btn" onClick={() => fetchReports(1)}>查询</button>
                    <button className="pcrt-reset-btn" onClick={handleResetFilters}>重置</button>
                    <button className="pcrt-export-btn" onClick={handleExport}>导出</button>
                </div>
            </div>

            {loading ? (
                <div className="pcrt-loading"><p>正在加载...</p></div>
            ) : reports.length === 0 ? (
                <div className="pcrt-empty"><p>暂无私车报表数据</p></div>
            ) : (
                <div className="pcrt-list">
                    {reports.map(report => (
                        <div key={report.applicationId} className="pcrt-item">
                            <div className="pcrt-item-header">
                                <span className="pcrt-plate">{report.plateNumber || '-'}</span>
                                <span className={`pcrt-status ${getStatusClass(report.approvalStatus || 0)}`}>
                                    {getStatusText(report.approvalStatus || 0)}
                                </span>
                            </div>
                            <div className="pcrt-details">
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">申请人:</span>
                                    <span className="pcrt-value">{report.applicantName}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">联系电话:</span>
                                    <span className="pcrt-value">{report.applicantPhone}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">所属部门:</span>
                                    <span className="pcrt-value">{report.applicantDept || '-'}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">申请单号:</span>
                                    <span className="pcrt-value">{report.applicationNo}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">申请时间:</span>
                                    <span className="pcrt-value">{report.applyTime}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">预计开始时间:</span>
                                    <span className="pcrt-value">{report.expectedStartTime}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">预计结束时间:</span>
                                    <span className="pcrt-value">{report.expectedEndTime}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">实际结束时间:</span>
                                    <span className="pcrt-value">{report.returnApplyTime}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">出发地:</span>
                                    <span className="pcrt-value">{report.expectedLocation || '-'}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">目的地:</span>
                                    <span className="pcrt-value">{report.destination || '-'}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">行驶里程:</span>
                                    <span className="pcrt-value">{report.totalMileage != null ? `${report.totalMileage} km` : '-'}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">用车费用:</span>
                                    <span className="pcrt-value">{report.amount != null ? `${report.amount} 元` : '-'}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">用车原因:</span>
                                    <span className="pcrt-value">{report.reason}</span>
                                </div>
                                <div className="pcrt-detail-item">
                                    <span className="pcrt-label">设备编号:</span>
                                    <span className="pcrt-value">{report.imei}</span>
                                </div>
                                {report.passengers && report.passengers.trim() !== '' && (
                                    <div className="pcrt-detail-item">
                                        <span className="pcrt-label">同行人:</span>
                                        <span className="pcrt-value">{report.passengers}</span>
                                    </div>
                                )}
                            </div>
                            <div className="pcrt-footer">
                                <button className="pcrt-detail-btn" onClick={() => handleReportDetail(report)}>用车详情</button>
                                {report.approvalStatus === 4 && (
                                    <button
                                        className="pcrt-btn-trace"
                                        onClick={() => handlePlayback(report)}
                                        disabled={!!traceLoading[report.applicationId]}
                                    >车辆轨迹</button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {loadingMore ? (
                <div className="pcrt-loading-more"><p>加载中...</p></div>
            ) : hasMore ? (
                <div className="pcrt-load-more">
                    <button className="load-more-btn" onClick={handleLoadMore}>
                        加载更多
                    </button>
                </div>
            ) : (
                reports.length > 0 && <div className="pcrt-no-more"><p>没有更多了</p></div>
            )}

            {/* PC端返回首页按钮（移动端隐藏） */}
            <div className="pcrt-back-home">
                <button className="back-btn" onClick={() => navigate('/')}>返回首页</button>
            </div>

            {/* 用车详情弹窗 */}
            {detailModalVisible && selectedReport && (
                <div className="pcrt-modal-mask" onClick={handleCloseModal}>
                    <div className="pcrt-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pcrt-modal-header">
                            <h3>用车详情_{selectedReport.plateNumber || ''}</h3>
                            <button className="pcrt-modal-close" onClick={handleCloseModal}>×</button>
                        </div>
                        <div className="pcrt-modal-body">
                            {[
                                ['申请人', selectedReport.applicantName],
                                ['联系电话', selectedReport.applicantPhone],
                                ['所属部门', selectedReport.applicantDept],
                                ['车牌号', selectedReport.plateNumber],
                                ['申请单号', selectedReport.applicationNo],
                                ['申请时间', selectedReport.applyTime],
                                ['结束时间', selectedReport.returnApplyTime],
                                ['预约用车时间', `${selectedReport.expectedStartTime} 至 ${selectedReport.expectedEndTime}`],
                                ['出发地', selectedReport.expectedLocation],
                                ['目的地', selectedReport.destination],
                                ['行驶里程', selectedReport.totalMileage != null ? `${selectedReport.totalMileage} km` : '-'],
                                ['用车费用', selectedReport.amount != null ? `${selectedReport.amount} 元` : '-'],
                                ...(selectedReport.passengers && selectedReport.passengers.trim() !== '' ? [['同行人', selectedReport.passengers]] : []),
                                ['用车原因', selectedReport.reason],
                                ['状态', getStatusText(selectedReport.approvalStatus)]
                            ].map(([label, value]) => (
                                <div className="pcrt-modal-row" key={label}>
                                    <span className="pcrt-modal-label">{label}</span>
                                    <span className="pcrt-modal-value">{value || '-'}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}
